import { useCallback, useReducer } from 'react';

import { uniq } from 'lodash-es';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  BulkTripValidateResults,
  OrderedMultiloadTripLocationDetail,
  useTripsBulkAssignCreateMutation,
  useTripsBulkValidateCreateMutation,
  useTripsMultiloadCreateMutation,
  useTripsMultiloadPartialUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { dispatcherSlice } from '../dispatcher.slice';
import { execTripMenuAction } from '../execTripMenuAction';
import { MultiloadPickupDropoffOrderItem } from './useMultiloadOrderItems';

type AssignState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'validation_error'; results: BulkTripValidateResults }
  | { status: 'server_error'; message: string };

type AssignAction =
  | { type: 'loading' }
  | { type: 'validation_error'; results: BulkTripValidateResults }
  | { type: 'server_error'; message: string };

function assignReducer(_state: AssignState, action: AssignAction): AssignState {
  switch (action.type) {
    case 'loading':
      return { status: 'loading' };
    case 'validation_error':
      return { status: 'validation_error', results: action.results };
    case 'server_error':
      return { status: 'server_error', message: action.message };
  }
}

class ValidationAbortError extends Error {
  constructor() {
    super('Bulk assignment failed due to validation');
    this.name = 'ValidationAbortError';
  }
}

export function useMultiloadAssign(
  orderedMultiLoadItems: MultiloadPickupDropoffOrderItem[]
) {
  const [state, dispatch] = useReducer(assignReducer, { status: 'idle' });
  const reduxDispatch = useAppDispatch();

  const {
    selectedTripIdsToMultiload,
    originatingTripId,
    assignmentIdToMultiload,
  } = useAppSelector((state) => state.dispatcher.multiloadAssignmentFlow);
  const unassignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.unassigned_trips
  );
  const assignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.assigned_trips
  );

  const [createTripMultiload] = useTripsMultiloadCreateMutation();
  const [updateTripMultiload] = useTripsMultiloadPartialUpdateMutation();
  const [bulkAssign] = useTripsBulkAssignCreateMutation();
  const [bulkValidate] = useTripsBulkValidateCreateMutation();

  const onAssignClick = useCallback(
    ({ shouldValidate = true }: { shouldValidate: boolean }) => {
      if (!(unassignedTrips && assignedTrips)) {
        return null;
      }

      let orderedTripLocationDetailIds: OrderedMultiloadTripLocationDetail[];
      try {
        orderedTripLocationDetailIds = orderedMultiLoadItems.map(
          (orderItem, index) => {
            const trip = orderItem.trip;
            const trip_location_detail_id =
              orderItem.type === 'pickup'
                ? trip.pickup_trip_location_detail_id
                : trip.dropoff_trip_location_detail_id;
            if (!trip_location_detail_id) {
              throw new Error('location detail not found');
            }

            return {
              trip_location_detail_id,
              order: index + 1,
            };
          }
        );
      } catch {
        dispatch({
          type: 'server_error',
          message:
            'Something went wrong preparing the multi-load. Please contact support at support@hibambi.com.',
        });
        return;
      }

      dispatch({ type: 'loading' });

      reduxDispatch(
        execTripMenuAction({
          action: async () => {
            // We probably also need for the assignment and multiload create to be all or nothing
            // For now, if bulk assign fails, the multiload request won't occur
            // If the multiload request fails, we should rollback the assignments
            // Currently bulk unassign only unassigns all trips in an assignment, which would
            // unassign more than the trips we're operating on which would be annoying for user
            try {
              const tripIdsToAssign = uniq([
                ...selectedTripIdsToMultiload,
                originatingTripId,
              ]);
              if (shouldValidate) {
                const validationResults = await bulkValidate({
                  bulkTripValidate: {
                    trip_ids: tripIdsToAssign,
                    assignment_id: assignmentIdToMultiload,
                    is_multiload: true,
                    ordered_trip_location_detail_ids:
                      orderedTripLocationDetailIds.map(
                        (orderedTripLocationDetail) =>
                          orderedTripLocationDetail.trip_location_detail_id
                      ),
                  },
                }).unwrap();

                if (validationResults.invalid_trips?.length) {
                  dispatch({
                    type: 'validation_error',
                    results: validationResults,
                  });
                  throw new ValidationAbortError();
                }
              }
              const result = await bulkAssign({
                bulkTripAssign: {
                  // The bulkAssign ep is smart enough to ignore trips that are already assigned
                  // Not sure about the bulk assign validate. That's for a future pr and shouldn't
                  // affect this anyway if the user chooses to override
                  // The uniq here is for completeness -- the ep actually ignores dupes already
                  trip_ids: tripIdsToAssign,
                  assignment_id: assignmentIdToMultiload,
                },
              }).unwrap();
              // Bulk unassign sends null on success
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              if (result && 'errors' in result) {
                dispatch({
                  type: 'server_error',
                  message: formatServerError(result),
                });
                throw new Error('Bulk assign failed');
              }
            } catch (error) {
              if (!(error instanceof ValidationAbortError)) {
                dispatch({
                  type: 'server_error',
                  message: formatServerError(error),
                });
              }
              // Rethrow so that execTripMenuAction doesn't think it succeeded
              throw error;
            }

            // Check if there's an existing multiload and patch it if so
            // This will fail if there are multiple multiloads that the user has brought in
            // We'll need to handle that eventually I think? The backend doesn't have validation
            // since that's in progress, but once that's done it may be handled
            const existingMultiLoadId = orderedMultiLoadItems.find(
              (orderedTripItem) => orderedTripItem.trip.multiload_id
            )?.trip.multiload_id;

            const mutation = existingMultiLoadId
              ? updateTripMultiload({
                  id: existingMultiLoadId,
                  patchedMultiload: {
                    multiload_trip_location_details:
                      orderedTripLocationDetailIds,
                  },
                })
              : createTripMultiload({
                  multiload: {
                    multiload_trip_location_details:
                      orderedTripLocationDetailIds,
                  },
                });

            try {
              const result = await mutation.unwrap();
              if ('errors' in result) {
                throw new Error(formatServerError(result));
              }
            } catch (error) {
              dispatch({
                type: 'server_error',
                message: formatServerError(error),
              });
              // Rethrow so that execTripMenuAction doesn't think it succeeded
              throw error;
            }

            reduxDispatch(dispatcherSlice.actions.onMultiloadFlowSuccess());
          },
          successMessage: 'Trips multi-loaded!',
          errorMessage: null,
        })
      );
    },
    [
      unassignedTrips,
      assignedTrips,
      orderedMultiLoadItems,
      reduxDispatch,
      bulkValidate,
      assignmentIdToMultiload,
      bulkAssign,
      selectedTripIdsToMultiload,
      originatingTripId,
      updateTripMultiload,
      createTripMultiload,
    ]
  );

  return {
    state,
    isLoading: state.status === 'loading',
    serverError: state.status === 'server_error' ? state.message : null,
    validationErrors:
      state.status === 'validation_error' ? state.results : null,
    onAssignClick,
  };
}
